import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../lib/api';
import { initPushNotifications } from '../lib/notifications';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('homeuni_user');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('homeuni_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('homeuni_user', JSON.stringify(res.data));
    } catch {
      localStorage.removeItem('homeuni_token');
      localStorage.removeItem('homeuni_user');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
    // Re-subscribe to push on every app load (ensures subscription stays fresh)
    if (localStorage.getItem('homeuni_token')) {
      initPushNotifications().catch(() => {});
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('homeuni_token', token);
    localStorage.setItem('homeuni_user', JSON.stringify(userData));
    setUser(userData);
    // Init push notifications after login
    initPushNotifications().catch(() => {});
    // Auto clock-in for staff
    if (!['Student', 'ProgrammeManager'].includes(userData.role)) {
      try { await api.post('/hr/clock/auto-in'); } catch (err) { console.error("Error:", err); }
    }
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithSession = async (sessionId) => {
    const res = await api.post('/auth/session', { session_id: sessionId });
    const { token, user: userData } = res.data;
    localStorage.setItem('homeuni_token', token);
    localStorage.setItem('homeuni_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { console.error("Error:", err); }
    localStorage.removeItem('homeuni_token');
    localStorage.removeItem('homeuni_user');
    setUser(null);
  };

  const value = { user, loading, login, loginWithGoogle, loginWithSession, logout, checkAuth };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export default AuthContext;
